import Loading from 'quasar/src/plugins/loading/Loading.js';
import { FindFileItemCommand, FindFilesInfoCommand } from "src/api/Common/Commands/FindFilesInfoCommand";
import { FileHelper } from "src/extensions/utils/FileHelper";
import { defineComponent, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { api } from "~/api";
import { adminApi } from "~/api/admin/Api";
import { GetFilesCommand } from "~/api/Common/Commands/GetFilesCommand";
import { FindFilesInfoResult } from "~/api/data/Common/FindFIlesInfoResult";
import BreadcrumbList from "~/components/BreadcrumbList.vue";
import { MsgBox } from "~/components/MsgBox";
import { Log } from "~/extensions/utils";
import { FileCode, FileFetchKb, PageID, SmsNotificationType } from "~/extensions/utils/DefineTypes";
import { AccountDetailValidator } from "~/pages/admin/Keiyakusha//Models/AccountDetailValidator";
import { AccountDetailViewModel } from "~/pages/admin/Keiyakusha/Models/AccountDetailViewModel";
import { commonStore } from "~/store/CommonStore";
const log = Log.create("AccountDetail");
export default defineComponent({
    name: "AccountDetail",
    components: {
        BreadcrumbList
    },
    setup() {
        const pageName = commonStore.pageName(PageID.accountEdit);
        const top = PageID.top;
        const keiyakushaList = PageID.keiyakushaList;
        const accountEdit = PageID.accountEdit;
        const accountData = ref(new AccountDetailViewModel());
        const valid = ref(new AccountDetailValidator(accountData.value));
        const router = useRouter();
        const route = useRoute();
        const userCd = Number(route.query.userCd);
        const notificationOptionList = [
            { label: "アカウント発行", value: SmsNotificationType.AccountIssuance },
            { label: "証明書申請", value: SmsNotificationType.ShomeiEntry },
            { label: "退去申請", value: SmsNotificationType.TaikyoEntry }
        ];
        const loginPdfName = ref("");
        const isPwd = ref(true);
        const shouldSearchOnBack = ref(false);
        //ファイル関連
        const loginFileCode = FileCode.LoginFile;
        const loginFileExists = ref(false);
        const findFilesInfoCommand = new FindFilesInfoCommand();
        const getFilesCommand = new GetFilesCommand();
        const result = ref(new FindFilesInfoResult());
        const zipFile = ref(null);
        let targetFile = ref();
        const fetchFiles = async () => {
            try {
                // ファイル情報取得パラメータ設定
                findFilesInfoCommand.UserCd = userCd;
                findFilesInfoCommand.fileList = [new FindFileItemCommand(FileFetchKb.Reganam, FileCode.LoginFile)];
                // ファイル情報取得
                result.value = await api.common.findFilesInfo(findFilesInfoCommand);
                targetFile.value = result.value.fileList.find(x => x.fileCode === FileCode.LoginFile);
                // ファイル取得パラメータ設定
                getFilesCommand.UserCd = userCd;
                getFilesCommand.fileList = [
                    {
                        fileNo: targetFile.value.fileNo,
                        fileFetchKb: FileFetchKb.Reganam,
                        fileCode: targetFile.value.fileCode,
                        fileExists: targetFile.value.fileExists
                    }
                ];
                // ファイル取得
                const res = await api.common.getFiles(getFilesCommand);
                zipFile.value = res;
                // 取得したファイルの存在フラグを代入
                loginFileExists.value = targetFile.value.fileExists ?? false;
            }
            catch (ex) {
                MsgBox.error(ex.message);
            }
            finally {
                Loading.hide();
            }
        };
        const downloadFileByCode = async () => {
            if (!targetFile.value.fileNo == null) {
                return;
            }
            // Zip内から対象ファイルを取得
            const fileData = await FileHelper.getFileFromZip(zipFile.value, targetFile.value.fileNo, targetFile.value?.fileCode);
            if (!fileData) {
                return;
            }
            FileHelper.downloadBlob(fileData.blob, fileData.fileName);
        };
        // 初期表示処理
        const initView = async () => {
            try {
                if (Number.isNaN(userCd)) {
                    await router.push({ name: "keiyakusha-list" });
                }
                Loading.show();
                let result = await adminApi.account.get(userCd);
                accountData.value = new AccountDetailViewModel(result);
                loginPdfName.value = `${accountData.value.userCd}_ログイン情報.pdf`;
                if (accountData.value.isRegisteredAccount) {
                    await fetchFiles();
                }
                valid.value = new AccountDetailValidator(accountData.value);
                valid.value.reset();
            }
            catch (ex) {
                log.error("get", ex);
                await MsgBox.error(ex.data.errorMessage);
                await router.push({ name: "keiyakusha-list" });
            }
            finally {
                Loading.hide();
            }
        };
        /** ハイフンを整形する */
        let formatTel = () => {
            if (accountData.value.isHojin) {
                return;
            }
            if (valid.value.tel.error) {
                return;
            }
            if (accountData.value.tel.length !== 13 && accountData.value.tel.length !== 0) {
                let temp = accountData.value.tel.replace("-", "");
                accountData.value.tel = temp.substring(0, 3) + "-" + temp.substring(3, 7) + "-" + temp.substring(7);
            }
        };
        const onClickDelete = async () => {
            const isDelete = await MsgBox.negativeConfirm(commonStore.messages.PI0005);
            if (isDelete) {
                try {
                    Loading.show();
                    await adminApi.account.delete(accountData.value.loginId);
                    Loading.hide();
                    await MsgBox.info(commonStore.messages.PI0006);
                    await router.push({ name: "keiyakusha-list", query: { isUpdated: "true" } });
                }
                catch (ex) {
                    log.error("delete", ex);
                    await MsgBox.error(ex.data.errorMessage);
                    await initView();
                }
                finally {
                    Loading.hide();
                }
            }
        };
        const onClickUpdate = async () => {
            if (!accountData.value.isRegisteredAccount || valid.value.error) {
                return;
            }
            const isUpdate = await MsgBox.confirm(commonStore.messages.PI0003);
            if (isUpdate) {
                try {
                    Loading.show();
                    //ハイフン対応
                    if (accountData.value.isKojin && accountData.value.tel.length !== 13) {
                        let temp = accountData.value.tel.replace("-", "");
                        accountData.value.tel = temp.substring(0, 3) + "-" + temp.substring(3, 7) + "-" + temp.substring(7);
                    }
                    await adminApi.account.update(accountData.value);
                    Loading.hide();
                    await MsgBox.info(commonStore.messages.PI0004);
                    await router.push({ name: "keiyakusha-list", query: { isUpdated: "true" } });
                }
                catch (ex) {
                    log.error("create", ex);
                    await MsgBox.error(ex.data.errorMessage);
                    // SMS関連のエラーの場合のみ初期表示処理を実行
                    if (ex.data.errorMessage.toLowerCase().includes("sms")) {
                        shouldSearchOnBack.value = true;
                        await initView();
                    }
                    else {
                        shouldSearchOnBack.value = false;
                    }
                }
                finally {
                    Loading.hide();
                }
            }
        };
        const onClickCreate = async () => {
            if (accountData.value.isRegisteredAccount || valid.value.error) {
                return;
            }
            const isCreate = await MsgBox.confirm(commonStore.messages.PI0001);
            if (isCreate) {
                try {
                    Loading.show();
                    //ハイフン対応
                    if (accountData.value.isKojin && accountData.value.tel.length !== 13) {
                        let temp = accountData.value.tel.replace("-", "");
                        accountData.value.tel = temp.substring(0, 3) + "-" + temp.substring(3, 7) + "-" + temp.substring(7);
                    }
                    await adminApi.account.create(accountData.value);
                    await MsgBox.info(commonStore.messages.PI0002);
                    await router.push({ name: "keiyakusha-list", query: { isUpdated: "true" } });
                }
                catch (ex) {
                    log.error("create", ex);
                    await MsgBox.error(ex.data.errorMessage);
                    // SMS関連のエラーの場合のみ初期表示処理を実行
                    if (ex.data.errorMessage.toLowerCase().includes("sms")) {
                        shouldSearchOnBack.value = true;
                        await initView();
                    }
                    else {
                        shouldSearchOnBack.value = false;
                    }
                }
                finally {
                    Loading.hide();
                }
            }
        };
        const onClickBack = async () => {
            // SMSエラーが直近で発生していた場合のみ再検索フラグ付きで戻る
            if (shouldSearchOnBack.value) {
                await router.push({ name: "keiyakusha-list", query: { isUpdated: "true" } });
                shouldSearchOnBack.value = false;
            }
            else {
                await router.push({ name: "keiyakusha-list" });
            }
        };
        initView();
        return {
            accountData,
            valid,
            notificationOptionList,
            loginPdfName,
            isPwd,
            loginFileCode,
            loginFileExists,
            pageName,
            top,
            keiyakushaList,
            accountEdit,
            onClickUpdate,
            onClickCreate,
            onClickDelete,
            formatTel,
            onClickBack,
            downloadFileByCode
        };
    }
});
